import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { firebaseAuth, firestore, storage } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, deleteDoc, setDoc } from '@firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from '@firebase/storage';
import './ProfileScreen.css';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { useHistory } from 'react-router-dom';

function ProfileScreen() {
  const dispatch = useDispatch();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState({
    profile: false,
    address: false,
    bank: false,
    business: false
  });
  const [editedData, setEditedData] = useState({});
  const [profileImageError, setProfileImageError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    type: 'Home',
    name: '',
    mobileNo: '',
    alternateMobileNo: '',
    fullAddress: '',
    locality: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    isPrimary: false
  });

  const userSignin = useSelector((state) => state.userSignin);
  const { userInfo } = userSignin;
  const history = useHistory();
  const [authUid, setAuthUid] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Build a Storage ref from a path or URL (ref supports gs:// and https URLs)
  const buildStorageRef = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    return ref(storage, pathOrUrl);
  };

  // Compress image on the client to <= maxBytes (default 200KB)
  const compressImage = (file, maxBytes = 200 * 1024, maxWidth = 600) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.onload = async () => {
          // Scale down keeping aspect ratio
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            const scale = maxWidth / width;
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Iteratively reduce quality to fit under maxBytes
          let quality = 0.9;
          const minQuality = 0.3;

          const toBlobAsync = (q) => new Promise((res) => canvas.toBlob(res, 'image/jpeg', q));
          let blob = await toBlobAsync(quality);
          while (blob && blob.size > maxBytes && quality > minQuality) {
            quality -= 0.1;
            blob = await toBlobAsync(Math.max(quality, minQuality));
          }
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }
          resolve(blob);
        };
        img.onerror = () => reject(new Error('Invalid image'));
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      } catch (err) {
        reject(err);
      }
    });
  };

  // Helpers
  const resolveUserId = (data) => {
    return (
      data?.id ||
      data?.uid ||
      data?.User_Id ||
      firebaseAuth.currentUser?.uid ||
      ''
    );
  };

  // ---- Profile Picture Handlers ----
  const handleProfilePictureSelect = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setNotification({ show: true, message: 'Please select a valid image file', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setNotification({ show: true, message: 'Image size should be less than 5MB', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
      return;
    }

    await uploadProfilePicture(file);
  };

  const uploadProfilePicture = async (file) => {
    try {
      if (!firebaseAuth.currentUser) throw new Error('Not signed in');
      const userId = firebaseAuth.currentUser.uid;
      setUploadingProfilePicture(true);

      // Compress to <= 200KB before upload
      const compressedBlob = await compressImage(file, 200 * 1024, 600);
      const uploadFile = new File([compressedBlob], `${userId}.jpg`, { type: 'image/jpeg' });

      // Upload new image
      const storageRef = ref(storage, `profile/${userId}.jpg`);
      const snapshot = await uploadBytes(storageRef, uploadFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Delete old image if exists
      if (userData?.Profile) {
        try {
          const oldRef = buildStorageRef(userData.Profile);
          if (oldRef) {
          await deleteObject(oldRef);
          }
        } catch (err) {
          console.warn('Old profile image cleanup skipped:', err?.message || err);
        }
      }

      // Update Firestore user document (try USERS first, fallback USER)
      let userRef = doc(firestore, 'USERS', userId);
      let userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        userRef = doc(firestore, 'USER', userId);
        userSnap = await getDoc(userRef);
      }
      if (!userSnap.exists()) throw new Error('User document not found');
      await updateDoc(userRef, { Profile: downloadURL });
      try {
        // Also update Firebase Auth profile for global consistency
        await updateProfile(firebaseAuth.currentUser, { photoURL: downloadURL });
      } catch (_) {}

      const updated = { ...(userData || {}), Profile: downloadURL };
      setUserData(updated);
      setProfileImageError(false);
      // Update Redux and localStorage so Header reflects immediately and persists across refresh
      try {
        dispatch({ type: 'USER_SIGNIN_SUCCESS', payload: updated });
        localStorage.setItem('userInfo', JSON.stringify(updated));
      } catch (_) {}
      setNotification({ show: true, message: 'Profile photo updated successfully!', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setNotification({ show: true, message: 'Error uploading profile picture', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const removeProfilePicture = async () => {
    try {
      if (!firebaseAuth.currentUser) throw new Error('Not signed in');
      const userId = firebaseAuth.currentUser.uid;
      setUploadingProfilePicture(true);

      if (userData?.Profile) {
        try {
          const imgRef = buildStorageRef(userData.Profile);
          if (imgRef) {
          await deleteObject(imgRef);
          }
        } catch (err) {
          console.warn('Profile image delete skipped:', err?.message || err);
        }
      }

      let userRef = doc(firestore, 'USERS', userId);
      let userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        userRef = doc(firestore, 'USER', userId);
        userSnap = await getDoc(userRef);
      }
      if (!userSnap.exists()) throw new Error('User document not found');
      await updateDoc(userRef, { Profile: '' });

      const updated = { ...(userData || {}), Profile: '' };
      setUserData(updated);
      setProfileImageError(false);
      try {
        dispatch({ type: 'USER_SIGNIN_SUCCESS', payload: updated });
        localStorage.setItem('userInfo', JSON.stringify(updated));
      } catch (_) {}
      setNotification({ show: true, message: 'Profile photo removed successfully!', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    } catch (error) {
      console.error('Error removing profile picture:', error);
      setNotification({ show: true, message: 'Error removing profile picture', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'Not available';
    try {
      let dateObj = null;
      // Firestore Timestamp
      if (value && typeof value === 'object' && typeof value.toDate === 'function') {
        dateObj = value.toDate();
      } else if (value && typeof value === 'object' && typeof value.seconds === 'number') {
        // Serialized Firestore Timestamp from localStorage (plain object)
        dateObj = new Date(value.seconds * 1000);
      } else if (typeof value === 'number') {
        // epoch ms or seconds
        dateObj = new Date(value < 1e12 ? value * 1000 : value);
      } else if (typeof value === 'string') {
        // Firebase Auth metadata creationTime is a string
        const d = new Date(value);
        if (!isNaN(d)) dateObj = d;
      }
      if (!dateObj) return 'Not available';
      return (
        dateObj.toLocaleDateString('en-GB') + ', ' +
        dateObj.toLocaleTimeString('en-GB', { hour12: false })
      );
    } catch (_) {
      return 'Not available';
    }
  };

  useEffect(() => {
    // Respond to Redux userInfo changes first
    if (userInfo) {
      setUserData(userInfo);
      setEditedData(userInfo);
      setLoading(false);
    } else if (firebaseAuth.currentUser) {
      // Fallback to Firebase auth data if Redux not populated yet
      const user = firebaseAuth.currentUser;
      const fallbackData = {
        Full_Name: user.displayName || user.email?.split('@')[0] || 'User',
        Email: user.email,
        id: user.uid,
        Role: 'User'
      };
      setUserData(fallbackData);
      setEditedData(fallbackData);
      setLoading(false);
    } else {
      // If neither Redux nor Firebase has a user, ensure local state is cleared and redirect home
      setUserData(null);
      setEditedData({});
      setLoading(false);
      if (window.location.pathname !== '/') {
        history.push('/');
      }
    }
  }, [userInfo]);

  useEffect(() => {
    // Listen to Firebase auth state to clear UI instantly on logout
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setAuthUid(user ? user.uid : null);
      if (!user && !userInfo) {
        setUserData(null);
        setEditedData({});
        setLoading(false);
        if (window.location.pathname !== '/') {
          history.push('/');
        }
      } else if (user && !userInfo) {
        // If user is present but Redux not yet, populate minimal info
        const fallbackData = {
          Full_Name: user.displayName || user.email?.split('@')[0] || 'User',
          Email: user.email || '',
          id: user.uid,
          Role: 'User'
        };
        setUserData(fallbackData);
        setEditedData(fallbackData);
        setLoading(false);
      } else if (user && userInfo) {
        // If we have Redux user but missing id, patch it so dependent effects (addresses) can run
        setUserData((prev) => {
          if (prev && !prev.id) {
            return { ...prev, id: user.uid };
          }
          return prev;
        });
      }
      // Mark that we've completed the initial auth check
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, [userInfo, history]);

  // Load addresses in Android structure: USERS/{uid}/USER_DATA/MY_ADDRESSES
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const uid = firebaseAuth.currentUser?.uid || authUid;
        if (!uid) {
          setAddresses([]);
          return;
        }
        const addrDocRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_ADDRESSES');
        const addrSnap = await getDoc(addrDocRef);
        if (!addrSnap.exists()) {
          setAddresses([]);
          return;
        }
        const data = addrSnap.data() || {};
        const size = Number(data.list_size) || 0;
        const parsed = [];
        for (let i = 1; i <= size; i++) {
          const idx = String(i);
          const item = {
            id: idx,
            pincode: data[`pincode_${idx}`] || '',
            flatNo: data[`flatNo_${idx}`] || '',
            locality: data[`locality_${idx}`] || '',
            city: data[`city_${idx}`] || '',
            landmark: data[`landmark_${idx}`] || '',
            name: data[`name_${idx}`] || '',
            mobileNo: data[`mobileNo_${idx}`] || '',
            alternateMobileNo: data[`alternateMobileNo_${idx}`] || '',
            state: data[`state_${idx}`] || '',
            type: data[`type_${idx}`] || 'Home',
            isPrimary: Boolean(data[`selected_${idx}`]) || false,
          };
          parsed.push(item);
        }
        setAddresses(parsed);
      } catch (err) {
        console.error('Error fetching addresses:', err);
        setAddresses([]);
      }
    };
    fetchAddresses();
  }, [authUid]);

  const handleEditToggle = (section) => {
    setEditMode(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    if (editMode[section]) {
      // Reset edited data when canceling
      setEditedData(userData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const userId = firebaseAuth.currentUser.uid;
      let ref = doc(firestore, 'USERS', userId);
      let snap = await getDoc(ref);
      
      if (!snap.exists()) {
        ref = doc(firestore, 'USER', userId);
        snap = await getDoc(ref);
      }

      if (!snap.exists()) {
        throw new Error('User document not found');
      }

      await updateDoc(ref, editedData);
      setUserData(editedData);
      setEditMode(prev => ({ ...prev, [section]: false }));
      setNotification({ show: true, message: 'Profile updated successfully!', type: 'success' });
      
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setNotification({ show: true, message: 'Error updating profile', type: 'error' });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'error' });
      }, 3000);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setNotification({ show: true, message: 'Copied to clipboard!', type: 'success' });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 2000);
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.name || !newAddress.mobileNo || !newAddress.fullAddress || !newAddress.city || !newAddress.pincode) {
      setNotification({ show: true, message: 'Please fill all required fields', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
      return;
    }

    try {
      const authUser = firebaseAuth.currentUser;
      if (!authUser) throw new Error('Not signed in');

      const userId = authUser.uid;
      const myAddressesRef = doc(firestore, 'USERS', userId, 'USER_DATA', 'MY_ADDRESSES');
      const myAddressesSnap = await getDoc(myAddressesRef);
      const existing = myAddressesSnap.exists() ? (myAddressesSnap.data() || {}) : { list_size: 0 };
      const currentSize = Number(existing.list_size) || 0;
      const newIndex = currentSize + 1;

      // Map our simple form to Android schema
      const nameVal = newAddress.name || '';
      const mobileVal = newAddress.mobileNo || '';

      const updates = {
        [`pincode_${newIndex}`]: newAddress.pincode,
        [`flatNo_${newIndex}`]: newAddress.fullAddress,
        [`locality_${newIndex}`]: newAddress.locality || '',
        [`city_${newIndex}`]: newAddress.city,
        [`landmark_${newIndex}`]: newAddress.landmark || '',
        [`name_${newIndex}`]: nameVal,
        [`mobileNo_${newIndex}`]: mobileVal,
        [`alternateMobileNo_${newIndex}`]: newAddress.alternateMobileNo || '',
        [`state_${newIndex}`]: newAddress.state || '',
        [`type_${newIndex}`]: newAddress.type,
        [`selected_${newIndex}`]: true,
        list_size: newIndex,
      };
      // Unselect previous primary if any
      addresses.forEach((addr) => {
        if (addr.isPrimary) {
          updates[`selected_${addr.id}`] = false;
        }
      });

      // Ensure doc exists then update
      if (!myAddressesSnap.exists()) {
        await setDoc(myAddressesRef, { list_size: 0 });
      }
      await updateDoc(myAddressesRef, updates);

      // Also reflect minimal visibility fields at USERS doc (cityN, stateN)
      try {
        const userDocRef = doc(firestore, 'USERS', userId);
        await updateDoc(userDocRef, {
          [`city${newIndex}`]: newAddress.city,
          [`state${newIndex}`]: newAddress.state || '',
        });
      } catch (_) {}

      // Refresh and local update
      const newAddrObj = {
        id: String(newIndex),
        pincode: newAddress.pincode,
        flatNo: newAddress.fullAddress,
        locality: newAddress.locality || '',
        city: newAddress.city,
        landmark: newAddress.landmark || '',
        name: nameVal,
        mobileNo: mobileVal,
        alternateMobileNo: newAddress.alternateMobileNo || '',
        state: newAddress.state || '',
        type: newAddress.type,
        isPrimary: true,
      };
      const updatedList = addresses.map(a => ({ ...a, isPrimary: false })).concat(newAddrObj);
      setAddresses(updatedList);

      // Reset form
      setNewAddress({ type: 'Home', name: '', mobileNo: '', alternateMobileNo: '', fullAddress: '', city: '', state: '', pincode: '', isPrimary: false });
      setShowAddAddressForm(false);
      setNotification({ show: true, message: 'Address added successfully!', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    } catch (error) {
      console.error('Error adding address:', error);
      setNotification({ show: true, message: 'Error adding address', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      const authUser = firebaseAuth.currentUser;
      if (!authUser) throw new Error('Not signed in');
      const uid = authUser.uid;
      const myAddressesRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_ADDRESSES');
      const snap = await getDoc(myAddressesRef);
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const size = Number(data.list_size) || 0;
      const removeIndex = parseInt(addressId, 10);
      if (isNaN(removeIndex)) return;

      const remaining = [];
      for (let i = 1; i <= size; i++) {
        if (i === removeIndex) continue;
        remaining.push({
          pincode: data[`pincode_${i}`] || '',
          flatNo: data[`flatNo_${i}`] || '',
          locality: data[`locality_${i}`] || '',
          city: data[`city_${i}`] || '',
          landmark: data[`landmark_${i}`] || '',
          name: data[`name_${i}`] || '',
          mobileNo: data[`mobileNo_${i}`] || '',
          alternateMobileNo: data[`alternateMobileNo_${i}`] || '',
          state: data[`state_${i}`] || '',
          selected: Boolean(data[`selected_${i}`]) || false,
        });
      }
      const newMap = { list_size: remaining.length };
      // Re-index 1..N and ensure at least one selected (keep first as primary if none selected)
      let anySelected = remaining.some(r => r.selected);
      remaining.forEach((r, idx) => {
        const ni = idx + 1;
        newMap[`pincode_${ni}`] = r.pincode;
        newMap[`flatNo_${ni}`] = r.flatNo;
        newMap[`locality_${ni}`] = r.locality;
        newMap[`city_${ni}`] = r.city;
        newMap[`landmark_${ni}`] = r.landmark;
        newMap[`name_${ni}`] = r.name;
        newMap[`mobileNo_${ni}`] = r.mobileNo;
        newMap[`alternateMobileNo_${ni}`] = r.alternateMobileNo;
        newMap[`state_${ni}`] = r.state;
        newMap[`type_${ni}`] = r.type || 'Home';
        newMap[`selected_${ni}`] = r.selected;
      });
      if (!anySelected && remaining.length > 0) {
        newMap[`selected_1`] = true;
      }
      await setDoc(myAddressesRef, newMap, { merge: false });

      // Update local state
      const parsed = remaining.map((r, idx) => ({
        id: String(idx + 1),
        ...r,
        isPrimary: idx === 0 ? (!anySelected ? true : r.selected) : r.selected,
      }));
      setAddresses(parsed);

      setNotification({ show: true, message: 'Address deleted successfully!', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    } catch (error) {
      console.error('Error deleting address:', error);
      setNotification({ show: true, message: 'Error deleting address', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 3000);
    }
  };

  const handleSetPrimary = async (addressId) => {
    try {
      const authUser = firebaseAuth.currentUser;
      if (!authUser) throw new Error('Not signed in');
      const uid = authUser.uid;
      const myAddressesRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_ADDRESSES');

      const updates = {};
      addresses.forEach((addr) => {
        updates[`selected_${addr.id}`] = (addr.id === addressId);
      });
      await updateDoc(myAddressesRef, updates);

      const updatedAddresses = addresses.map(addr => ({ ...addr, isPrimary: addr.id === addressId }));
      setAddresses(updatedAddresses);
      setNotification({ show: true, message: 'Primary address updated!', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    } catch (error) {
      console.error('Error updating primary address:', error);
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  // If no user is signed in, trigger redirect effect above and render nothing
  if (!userData) {
    return null;
  }

  return (
    <div className="profile-container">
      <h1 className="profile-title">User Profile</h1>
      
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="profile-grid">
        {/* User Profile Card */}
        <div className="profile-card user-profile-card">
          <div className="card-header">
            <div className="card-header-left">
              <i className="fas fa-user-circle card-icon"></i>
              <span>User Profile</span>
            </div>
            <button 
              className={`edit-btn ${editMode.profile ? 'cancel' : 'edit'}`}
              onClick={() => handleEditToggle('profile')}
            >
              {editMode.profile ? 'CANCEL' : 'EDIT'}
            </button>
          </div>
          
          <div className="card-content">
            {editMode.profile ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="Full_Name"
                    value={editedData.Full_Name || ''}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                  />
                </div>
                <div className="form-group">
                  <label>Mobile</label>
                  <input
                    type="text"
                    name="Mobile_No"
                    value={editedData.Mobile_No || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleInputChange({ target: { name: 'Mobile_No', value: val } });
                    }}
                    placeholder="Enter mobile number"
                    maxLength="10"
                  />
                </div>
                <button 
                  className="save-btn"
                  onClick={() => handleSave('profile')}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <div className="user-profile-content">
                <div className="profile-avatar-section">
                  <div className="avatar-wrapper">
                    {userData.Profile && userData.Profile !== "" && !profileImageError ? (
                      <img 
                        className="profile-avatar"
                        src={userData.Profile}
                        alt={userData.Full_Name || 'User'}
                        onError={() => setProfileImageError(true)}
                      />
                    ) : (
                      <div className="profile-avatar-fallback">
                        {(userData.Full_Name || userData.Email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    {uploadingProfilePicture && (
                      <div className="avatar-overlay">
                        <div className="loading-spinner small"></div>
                        <span>Processing...</span>
                      </div>
                    )}
                  </div>
                  <div className="role-chip">
                    {userData.Role || 'User'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'center' }}>
                    <label htmlFor="profile-picture-input" className="set-primary-btn" style={{ cursor: uploadingProfilePicture ? 'not-allowed' : 'pointer', opacity: uploadingProfilePicture ? 0.7 : 1 }}>
                      <i className="fas fa-camera" style={{ marginRight: 6 }}></i>
                      {uploadingProfilePicture ? 'Uploading...' : (userData.Profile ? 'Change Photo' : 'Add Photo')}
                    </label>
                    <input
                      id="profile-picture-input"
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureSelect}
                      style={{ display: 'none' }}
                    />
                    {userData.Profile && (
                      <button className="delete-btn" onClick={removeProfilePicture} disabled={uploadingProfilePicture}>
                        <i className="fas fa-trash" style={{ marginRight: 6 }}></i>
                        {uploadingProfilePicture ? 'Please wait...' : 'Remove'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="profile-details">
                  <div className="profile-name">
                    <strong>Name: {userData.Full_Name || userData.displayName || 'Not set'}</strong>
                  </div>
                  
                  <div className="profile-contact">
                    <div className="contact-item">
                      <i className="fas fa-envelope"></i>
                      <span>{userData.Email || userData.email || 'No email'}</span>
                    </div>
                    <div className="contact-item">
                      <i className="fas fa-phone"></i>
                      <span>{userData.Mobile_No ? `+91 ${userData.Mobile_No}` : 'Mobile not set'}</span>
                    </div>
                  </div>

                  <div className="user-id-section">
                    <span className="user-id-label">ID:</span>
                    <span className="user-id-value">{resolveUserId(userData) || 'Not available'}</span>
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(resolveUserId(userData) || '')}
                      title="Copy User ID"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>

                  <div className="member-since">
                    Member since: {
                      formatDateTime(
                        userData?.Member_Since ||
                        userData?.memberSince ||
                        userData?.Created_At ||
                        userData?.createdAt ||
                        userData?.Account_Created_On ||
                        firebaseAuth.currentUser?.metadata?.creationTime
                      )
                    }
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Registered Address Card */}
        <div className="profile-card address-card">
          <div className="card-header">
            <div className="card-header-left">
              <i className="fas fa-home card-icon"></i>
              <span>Registered Address</span>
            </div>
            <button 
              className={`edit-btn ${editMode.address ? 'cancel' : 'edit'}`}
              onClick={() => handleEditToggle('address')}
            >
              {editMode.address ? 'CANCEL' : 'EDIT'}
            </button>
          </div>
          
          <div className="card-content">
            {editMode.address ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Full Address</label>
                  <textarea
                    name="Full_Address"
                    value={editedData.Full_Address || ''}
                    onChange={handleInputChange}
                    placeholder="Enter full address"
                    rows="3"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="City"
                      value={editedData.City || ''}
                      onChange={handleInputChange}
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="State"
                      value={editedData.State || ''}
                      onChange={handleInputChange}
                      placeholder="State"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="Country"
                      value={editedData.Country || ''}
                      onChange={handleInputChange}
                      placeholder="Country"
                    />
                  </div>
                  <div className="form-group">
                    <label>Pincode</label>
                    <input
                      type="text"
                      name="Pincode"
                      value={editedData.Pincode || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        handleInputChange({ target: { name: 'Pincode', value: val } });
                      }}
                      placeholder="Pincode"
                      maxLength="6"
                    />
                  </div>
                </div>
                <button 
                  className="save-btn"
                  onClick={() => handleSave('address')}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <div className="address-info">
                <div className="info-section">
                  <label>Full Address</label>
                  <p>{userData.Full_Address || 'Not set'}</p>
                </div>
                <div className="info-row">
                  <div className="info-section">
                    <label>City</label>
                    <p>{userData.City || 'Not set'}</p>
                  </div>
                  <div className="info-section">
                    <label>State</label>
                    <p>{userData.State || 'Not set'}</p>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-section">
                    <label>Country</label>
                    <p>{userData.Country || 'Not set'}</p>
                  </div>
                  <div className="info-section">
                    <label>Pincode</label>
                    <p>{userData.Pincode || 'Not set'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bank Details Card */}
        <div className="profile-card bank-card">
          <div className="card-header">
            <div className="card-header-left">
              <i className="fas fa-university card-icon"></i>
              <span>Bank Details</span>
            </div>
            <button 
              className={`edit-btn ${editMode.bank ? 'cancel' : 'edit'}`}
              onClick={() => handleEditToggle('bank')}
            >
              {editMode.bank ? 'CANCEL' : 'EDIT'}
            </button>
          </div>
          
          <div className="card-content">
            {editMode.bank ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    name="Account_Holder_Name"
                    value={editedData.Account_Holder_Name || ''}
                    onChange={handleInputChange}
                    placeholder="Account holder name"
                  />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    name="Account_No"
                    value={editedData.Account_No || ''}
                    onChange={handleInputChange}
                    placeholder="Account number"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      name="Bank_Name"
                      value={editedData.Bank_Name || ''}
                      onChange={handleInputChange}
                      placeholder="Bank name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Branch Name</label>
                    <input
                      type="text"
                      name="Branch_Name"
                      value={editedData.Branch_Name || ''}
                      onChange={handleInputChange}
                      placeholder="Branch name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    name="IFSC"
                    value={editedData.IFSC || ''}
                    onChange={handleInputChange}
                    placeholder="IFSC code"
                  />
                </div>
                <button 
                  className="save-btn"
                  onClick={() => handleSave('bank')}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <div className="bank-info">
                <div className="info-section">
                  <label>Account Holder Name</label>
                  <p>{userData.Account_Holder_Name || 'Not set'}</p>
                </div>
                <div className="info-section">
                  <label>Account Number</label>
                  <p>{userData.Account_No || 'Not set'}</p>
                </div>
                <div className="info-row">
                  <div className="info-section">
                    <label>Bank Name</label>
                    <p>{userData.Bank_Name || 'Not set'}</p>
                  </div>
                  <div className="info-section">
                    <label>Branch Name</label>
                    <p>{userData.Branch_Name || 'Not set'}</p>
                  </div>
                </div>
                <div className="info-section">
                  <label>IFSC Code</label>
                  <p>{userData.IFSC || 'Not set'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Business Information Card - Only for Retailers/Sellers */}
        {userData.Role && (userData.Role.toLowerCase() === 'retailer' || userData.Role.toLowerCase() === 'seller') && (
          <div className="profile-card business-card">
            <div className="card-header">
              <div className="card-header-left">
                <i className="fas fa-building card-icon"></i>
                <span>Business Information</span>
              </div>
              <button 
                className={`edit-btn ${editMode.business ? 'cancel' : 'edit'}`}
                onClick={() => handleEditToggle('business')}
              >
                {editMode.business ? 'CANCEL' : 'EDIT'}
              </button>
            </div>
            
            <div className="card-content">
              {editMode.business ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Shop Name</label>
                    <input
                      type="text"
                      name="Shop_Name"
                      value={editedData.Shop_Name || ''}
                      onChange={handleInputChange}
                      placeholder="Shop name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Business Proof No (GST)</label>
                    <input
                      type="text"
                      name="Business_Proof_No"
                      value={editedData.Business_Proof_No || ''}
                      onChange={handleInputChange}
                      placeholder="GST number"
                    />
                  </div>
                  <button 
                    className="save-btn"
                    onClick={() => handleSave('business')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              ) : (
                <div className="business-info">
                  <div className="info-section">
                    <label>Shop Name</label>
                    <p>{userData.Shop_Name || 'Not set'}</p>
                  </div>
                  <div className="info-section">
                    <label>Business Proof No (GST)</label>
                    <p>{userData.Business_Proof_No || 'Not set'}</p>
                  </div>
                  {userData.Business_Proof_Image && (
                    <div className="info-section">
                      <label>Business Proof Document</label>
                      <a 
                        href={userData.Business_Proof_Image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-document-btn"
                      >
                        View Document
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pickup & Return Addresses Section */}
      <div className="addresses-section">
        <div className="addresses-header">
          <div className="addresses-title">
            <i className="fas fa-map-marker-alt"></i>
            <span>Pickup & Return Addresses</span>
          </div>
          <button 
            className="add-address-btn"
            onClick={() => setShowAddAddressForm(true)}
          >
            <i className="fas fa-plus"></i>
            ADD ADDRESS
          </button>
        </div>

        {showAddAddressForm && (
          <>
            {/* Modal Backdrop */}
            <div className="modal-backdrop" onClick={() => setShowAddAddressForm(false)}></div>

            {/* Modal Dialog */}
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Add New Address</h3>
                  <button
                    className="modal-close-btn"
                    onClick={() => setShowAddAddressForm(false)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Address Type</label>
                      <select
                        value={newAddress.type}
                        onChange={(e) => setNewAddress({...newAddress, type: e.target.value})}
                      >
                        <option value="Home">Home</option>
                        <option value="Office">Office</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      {userData?.Role && userData.Role.toLowerCase() === 'retailer' ? 'Shop Name *' : 'Name *'}
                    </label>
                    <input
                      type="text"
                      value={newAddress.name}
                      onChange={(e) => setNewAddress({...newAddress, name: e.target.value})}
                      placeholder={userData?.Role && userData.Role.toLowerCase() === 'retailer' ? 'Enter shop name' : 'Enter name'}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Full Address *</label>
                    <textarea
                      value={newAddress.fullAddress}
                      onClick={(e) => setNewAddress({...newAddress, fullAddress: e.target.value})}
                      onChange={(e) => setNewAddress({...newAddress, fullAddress: e.target.value})}
                      placeholder="Flat/House No., Building, Area"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Locality</label>
                      <input
                        type="text"
                        value={newAddress.locality}
                        onChange={(e) => setNewAddress({...newAddress, locality: e.target.value})}
                        placeholder="Locality"
                      />
                    </div>
                    <div className="form-group">
                      <label>Landmark</label>
                      <input
                        type="text"
                        value={newAddress.landmark}
                        onChange={(e) => setNewAddress({...newAddress, landmark: e.target.value})}
                        placeholder="Landmark (optional)"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={newAddress.state}
                        onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                        placeholder="State"
                      />
                    </div>
                    <div className="form-group">
                      <label>Pincode *</label>
                      <input
                        type="text"
                        value={newAddress.pincode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setNewAddress({...newAddress, pincode: val});
                        }}
                        placeholder="Pincode"
                        maxLength="6"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Mobile Number *</label>
                      <input
                        type="text"
                        value={newAddress.mobileNo}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setNewAddress({...newAddress, mobileNo: val});
                        }}
                        placeholder="10-digit mobile number"
                        maxLength="10"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Alternate Mobile No</label>
                      <input
                        type="text"
                        value={newAddress.alternateMobileNo}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setNewAddress({...newAddress, alternateMobileNo: val});
                        }}
                        placeholder="Alternate mobile (10 digits)"
                        maxLength="10"
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="modal-cancel-btn"
                    onClick={() => setShowAddAddressForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-save-btn"
                    onClick={handleAddAddress}
                  >
                    Save Address
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="addresses-grid">
          {/* Dynamic addresses */}
          {addresses.map((address) => (
            <div key={address.id} className={`address-card ${address.isPrimary ? 'primary' : ''}`}>
              <div className="address-header">
                <div className="address-type">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    {address.name ? address.name : 'Address'}
                    {address.type && <span className="address-type-label"> ({address.type})</span>}
                  </span>
                </div>
                <div className="address-actions">
                  {address.isPrimary ? (
                    <button className="primary-btn">Primary</button>
                  ) : (
                    <button
                      className="set-primary-btn"
                      onClick={() => handleSetPrimary(address.id)}
                    >
                      SET AS PRIMARY
                    </button>
                  )}
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteAddress(address.id)}
                  >
                    <i className="fas fa-trash"></i>
                    DELETE
                  </button>
                </div>
              </div>
              <div className="address-content">
                <p>
                  {[address.flatNo, address.locality, address.landmark]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                <p>
                  {[address.city, address.state].filter(Boolean).join(', ')}
                  {address.pincode ? ` - ${address.pincode}` : ''}
                </p>
                {(address.name || address.mobileNo || address.alternateMobileNo) && (
                  <p>{[address.name, address.mobileNo].filter(Boolean).join(' - ')}
                  {address.alternateMobileNo ? ` (Alternate No: +91 ${address.alternateMobileNo})` : ''}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProfileScreen;
